import type { SupportedLanguage } from '../lib/i18n/constants';

// =============================================================================
// TIMESTAMP TYPES (Supabase uses ISO strings natively)
// =============================================================================

/**
 * Timestamp type - Supabase returns and accepts ISO strings.
 * Use `new Date(timestamp)` when you need a Date object.
 */
export type Timestamp = string;

/**
 * Create an ISO timestamp string for Supabase.
 */
export function createTimestamp(date: Date = new Date()): string {
  return date.toISOString();
}

// =============================================================================
// PLAN CONFIGURATION - Re-exported from central config
// =============================================================================

export {
  PLAN_CONFIGS,
  TRIAL_DAYS,
  TRIAL_AI_REQUESTS_PER_DAY,
  getAiDailyLimit,
  isSubscriptionActive as isSubscriptionStatusActive,
  type PlanType,
  type PlanFeatures,
  type PlanConfig,
  type SubscriptionStatusType,
} from '../config/plans';

import {
  PLAN_CONFIGS,
  TRIAL_DAYS,
  type PlanType,
  type PlanFeatures,
  type SubscriptionStatusType,
} from '../config/plans';

// =============================================================================
// ORGANIZATION (TENANT)
// =============================================================================

/**
 * Represents an organization (tenant) in the multi-tenant system.
 * Each organization has its own projects, members, and billing.
 */
export interface Organization {
  id: string;
  name: string;
  slug: string;
  ownerId: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  stripeCustomerId?: string;
  subscription: SubscriptionStatus;
  settings: OrgSettings;
}

/**
 * Organization settings that can be customized per tenant.
 */
export interface OrgSettings {
  logoUrl?: string;
  primaryColor?: string;
  defaultLanguage: SupportedLanguage;
  defaultCurrency: string;
  allowMemberInvites: boolean;
}

/**
 * Default settings for new organizations.
 */
export const DEFAULT_ORG_SETTINGS: OrgSettings = {
  defaultLanguage: 'fr',
  defaultCurrency: 'EUR',
  allowMemberInvites: true,
};

// =============================================================================
// ORGANIZATION MEMBERS
// =============================================================================

/**
 * Roles available for organization members.
 */
export type MemberRole = 'owner' | 'admin' | 'member' | 'viewer';

/**
 * Represents a member of an organization.
 */
export interface OrganizationMember {
  id: string;
  email: string;
  displayName?: string;
  photoUrl?: string;
  role: MemberRole;
  joinedAt: Timestamp;
  invitedBy?: string;
}

/**
 * Permissions associated with each role.
 */
export const ROLE_PERMISSIONS: Record<
  MemberRole,
  {
    canManageMembers: boolean;
    canManageBilling: boolean;
    canCreateProjects: boolean;
    canEditProjects: boolean;
    canDeleteProjects: boolean;
    canDeleteOrg: boolean;
  }
> = {
  owner: {
    canManageMembers: true,
    canManageBilling: true,
    canCreateProjects: true,
    canEditProjects: true,
    canDeleteProjects: true,
    canDeleteOrg: true,
  },
  admin: {
    canManageMembers: true,
    canManageBilling: true,
    canCreateProjects: true,
    canEditProjects: true,
    canDeleteProjects: true,
    canDeleteOrg: false,
  },
  member: {
    canManageMembers: false,
    canManageBilling: false,
    canCreateProjects: true,
    canEditProjects: true,
    canDeleteProjects: false,
    canDeleteOrg: false,
  },
  viewer: {
    canManageMembers: false,
    canManageBilling: false,
    canCreateProjects: false,
    canEditProjects: false,
    canDeleteProjects: false,
    canDeleteOrg: false,
  },
};

// =============================================================================
// SUBSCRIPTION & BILLING
// =============================================================================

/**
 * Billing interval for subscriptions.
 */
export type BillingInterval = 'month' | 'year';

/**
 * Current subscription status for an organization.
 */
export interface SubscriptionStatus {
  plan: PlanType;
  status: SubscriptionStatusType;
  billingInterval?: BillingInterval;
  trialEndsAt?: Timestamp;
  currentPeriodStart?: Timestamp;
  currentPeriodEnd?: Timestamp;
  cancelAtPeriodEnd?: boolean;
  stripeSubscriptionId?: string;
  stripePriceId?: string;
  features: PlanFeatures;
}

/**
 * Default subscription for new organizations (free trial).
 */
export function createDefaultSubscription(trialDays: number = TRIAL_DAYS): SubscriptionStatus {
  const now = new Date();
  const trialEnd = new Date(now.getTime() + trialDays * 24 * 60 * 60 * 1000);

  return {
    plan: 'starter',
    status: 'trialing',
    trialEndsAt: createTimestamp(trialEnd),
    features: PLAN_CONFIGS.starter.features,
  };
}

// =============================================================================
// INVITATIONS
// =============================================================================

/**
 * Invitation status.
 */
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

/**
 * Represents a pending invitation to join an organization.
 */
export interface Invitation {
  id: string;
  orgId: string;
  orgName: string;
  email: string;
  role: MemberRole;
  token: string;
  status: InvitationStatus;
  invitedBy: string;
  invitedByName?: string;
  createdAt: Timestamp;
  expiresAt: Timestamp;
  acceptedAt?: Timestamp;
}

/**
 * Create invitation expiration date (7 days from now).
 */
export function createInvitationExpiry(days: number = 7): Timestamp {
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + days);
  return createTimestamp(expiryDate);
}

// =============================================================================
// USER EXTENSIONS
// =============================================================================

/**
 * Extended user data for multi-tenancy.
 */
export interface UserTenantData {
  defaultOrgId?: string;
  organizations: string[];
}

// =============================================================================
// PROJECT EXTENSIONS
// =============================================================================

/**
 * Extended project data for multi-tenancy.
 */
export interface ProjectTenantData {
  orgId: string;
  createdBy: string;
  lastModifiedBy?: string;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Generate a URL-friendly slug from a name.
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50);
}

/**
 * Generate a unique slug by appending a random suffix.
 */
export function generateUniqueSlug(name: string): string {
  const baseSlug = generateSlug(name);
  const suffix = Math.random().toString(36).substring(2, 8);
  return `${baseSlug}-${suffix}`;
}

/**
 * Check if a user has permission for an action based on their role.
 */
export function hasPermission(
  role: MemberRole,
  action: keyof (typeof ROLE_PERMISSIONS)[MemberRole]
): boolean {
  return ROLE_PERMISSIONS[role][action];
}

/**
 * Check if the organization can add more members based on plan limits.
 */
export function canAddMember(
  subscription: SubscriptionStatus,
  currentMemberCount: number
): boolean {
  const maxMembers = subscription.features.maxMembers;
  return maxMembers === -1 || currentMemberCount < maxMembers;
}

/**
 * Check if the organization can create more projects based on plan limits.
 */
export function canCreateProject(
  subscription: SubscriptionStatus,
  currentProjectCount: number
): boolean {
  const maxProjects = subscription.features.maxProjects;
  return maxProjects === -1 || currentProjectCount < maxProjects;
}

/**
 * Check if the subscription is active (can use features).
 */
export function isSubscriptionActive(subscription: SubscriptionStatus): boolean {
  if (subscription.status === 'active') return true;
  if (subscription.status !== 'trialing') return false;
  if (!subscription.trialEndsAt) return true;
  return new Date(subscription.trialEndsAt).getTime() > Date.now();
}

/**
 * Check if a user can create more organizations based on their existing orgs.
 */
export function canCreateOrganization(
  ownedOrganizations: { subscription: SubscriptionStatus }[]
): boolean {
  if (ownedOrganizations.length === 0) return true;
  const hasAnyPaidOrg = ownedOrganizations.some((org) => org.subscription.status === 'active');
  if (hasAnyPaidOrg) return true;
  return false;
}

/**
 * Get the maximum number of organizations a user can own.
 */
export function getMaxOwnedOrganizations(
  ownedOrganizations: { subscription: SubscriptionStatus }[]
): number {
  const hasAnyPaidOrg = ownedOrganizations.some((org) => org.subscription.status === 'active');
  return hasAnyPaidOrg ? -1 : 1;
}
